'use client';

import { useEffect, useRef } from 'react';

import {
  type ReceiptsPlacement,
  trackReceiptLinkClicked,
  trackReceiptsViewed,
} from '~/lib/analytics/posthog-events';

/**
 * Client-side instrumentation for the receipts layer (experiment E1).
 *
 * PostHog is initialized elsewhere (consent-gated provider); these components
 * only capture through the shared null-safe helpers in
 * lib/analytics/posthog-events.ts, so they no-op when tracking is absent or
 * the visitor has not consented. No layout, no animation, no gate.
 */

/**
 * Invisible in-view sentinel. Observes its parent element (the receipts
 * surface it sits inside; the sentinel itself has zero size) and fires
 * `selenos_receipts_viewed` once when at least a quarter of that surface is
 * visible (a tall section can exceed the mobile viewport, so a higher
 * threshold could never fire), then disconnects.
 */
export function ReceiptsInView({
  placement,
}: {
  placement: ReceiptsPlacement;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const fired = useRef(false);

  useEffect(() => {
    const target = ref.current?.parentElement;
    if (!target || fired.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (fired.current) return;
        if (entries.some((entry) => entry.isIntersecting)) {
          fired.current = true;
          trackReceiptsViewed(placement);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [placement]);

  return <span ref={ref} aria-hidden="true" />;
}

/**
 * Anchor to the receipts section that records the click with its placement.
 * Plain in-page link: no gate, no navigation interception.
 */
export function ReceiptAnchor({
  placement,
  className,
  children,
}: React.PropsWithChildren<{
  placement: ReceiptsPlacement;
  className?: string;
}>) {
  return (
    <a
      href="#receipts"
      className={className}
      onClick={() => trackReceiptLinkClicked(placement)}
    >
      {children}
    </a>
  );
}
